import React, { useEffect, useState } from 'react';
import { FaWifi } from 'react-icons/fa';

/**
 * Tells the listener when the app is running on what it has saved.
 *
 * Without this, being offline looks like being online but broken. The globe
 * still turns, favourites are still there and the station count still reads
 * 62,000 — all served from the cache — so a search that quietly returns nothing
 * reads as "this app is rubbish" rather than "you have no signal". Saying so
 * plainly is the difference.
 *
 * `navigator.onLine` is not a promise that the internet works — it only means a
 * network interface is up, so a captive wifi portal still reports true. It is
 * reliable in the direction that matters here: when it says offline, it is.
 */
const OfflineNotice: React.FC = () => {
  const [offline, setOffline] = useState(
    typeof navigator !== 'undefined' && navigator.onLine === false
  );

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-16 left-0 right-0 z-50 bg-amber-500/95 text-black px-3 py-1.5
                 text-[11px] md:text-xs font-medium flex items-center justify-center gap-2
                 backdrop-blur-sm shadow-sm"
    >
      <FaWifi className="flex-shrink-0 opacity-70" size={12} />
      <span className="text-center">
        You&apos;re offline — showing saved stations only. Search and playback need a connection.
      </span>
    </div>
  );
};

export default OfflineNotice;
